/*
 * File created on May 28, 2021
 *
 * Copyright (c) 2021 Virginia Polytechnic Institute and State University
 * and others as noted
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import './ConfirmDialog.scss';

/**
 * A component that displays a modal dialog to prompt the user to confirm an
 * action.
 *
 * @param show flag that controls whether the modal dialog is displayed.
 * @param title title for the top bar of the dialog
 * @param children components to nest within the dialog. Often this is just
 *    a fragment that displays a question describing that which is to be
 *    confirmed
 * @param confirmLabel label for the button that provides the affirmative,
 *    confirming response; the default label is 'Yes'.
 * @param dismissLabel label for the button that provids the negative,
 *    non-confirming response; the default label is 'No'.
 * @param confirmVariant button variant for the button that provides the
 *    affirmative, confirming response; the default variant is 'primary'.
 * @param onOutcome a callback function with signature
 *    `handler(confirmed: boolean, state); this function is invoked when the
 *    user responds by click one of the two buttons or dismisses the dialog
 *    using the close control in the upper-right corner
 * @param state arbitrary state to be passed to the `onOutcome` callback
 * @param className additional CSS class name(s) for the component
 * @returns component fragment
 */
function ConfirmDialog({
  show,
  title,
  children,
  confirmLabel = 'Yes',
  dismissLabel = 'No',
  confirmVariant = 'primary',
  onOutcome = () => {},
  state,
  className = '',
}) {
  if (!show) return null;
  return (
    show && (
      <Modal
        className={`confirm-dialog ${className}`}
        show={true}
        onHide={() => {
          onOutcome(false, state);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{children}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              onOutcome(false, state);
            }}
          >
            {dismissLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={() => {
              onOutcome(true, state);
            }}
          >
            {confirmLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  );
}

export default ConfirmDialog;
